import {
	ButtonUtility,
	PageHeader,
	PaginationPageDefault,
	resolveTableViewState,
	STATUS_BADGE_TONE_PALETTES,
	StatusBadge,
	Table,
	TableCard,
	type TableColumn,
	TableStateRow,
} from "@components";
import { Button } from "@components/button/button-untitledui";
import { usePaginationTotal } from "@components/pagination/use-pagination-total";
import {
	ArrowRight,
	Attachment02,
	CursorClick02,
	File05,
	GitBranch01,
	List,
	Signal01,
} from "@untitledui/icons";
import dayjs from "dayjs";
import { type Key, type ReactNode, useMemo, useState } from "react";
import { mapOdometerApiSourceToHistoryOrigin } from "@/features/vehicle/handlers";
import {
	useGetVehicleOdometerDetail,
	useGetVehicleOdometerHistory,
	useGetVehicleOdometerLatestChecklist,
	useGetVehicleOdometerSummary,
} from "@/features/vehicle/query";
import type {
	OdometerHistoryItem,
	OdometerHistoryOrigin,
	VehicleOdometerHistoryItemResponse,
} from "@/features/vehicle/types/odometer-ui.types";
import { VEHICLE_ODOMETER_SOURCE } from "@/features/vehicle/types/odometer-ui.types";
import { cx } from "@/utils/cx";
import { OdometerAttachmentPreviewModal } from "./odometer-attachment-preview-modal";
import { OdometerChecklistUpdateModal } from "./odometer-checklist-update-modal";
import { OdometerManualUpdateDrawer } from "./odometer-manual-update-drawer";

type OdometerTabContentProps = {
	currentOdometer: number | null;
	vehicleId: string;
	vehicleHeaderSubtitle: string;
};

const HISTORY_ORIGIN_OPTIONS: OdometerHistoryOrigin[] = [
	"TELEMETRIA",
	"CHECKLIST",
	"MANUAL",
];
const HISTORY_PAGE_SIZE = 5;
const TELEMETRY_HIRE_WHATSAPP_URL =
	"https://wa.me/556296655802?text=Ol%C3%A1%2C%20quero%20contratar%20Telemetria.";

function SourceCard({
	icon,
	title,
	description,
	rightSlot,
}: {
	icon: ReactNode;
	title: string;
	description: string;
	rightSlot?: ReactNode;
}) {
	return (
		<div className="flex flex-col gap-3 rounded-[4px] bg-[#f1f4f6] px-4 py-4 md:flex-row md:items-center md:justify-between">
			<div className="flex items-start gap-3">
				<div className="mt-0.5 text-fg-primary">{icon}</div>
				<div className="flex flex-col gap-1">
					<p className="font-bold text-fg-primary text-sm">{title}</p>
					<p className="text-fg-secondary text-xs">{description}</p>
				</div>
			</div>
			{rightSlot}
		</div>
	);
}

function HistoryOriginBadge({ origin }: { origin: OdometerHistoryOrigin }) {
	const iconByOrigin: Record<OdometerHistoryOrigin, ReactNode> = {
		TELEMETRIA: <Signal01 aria-hidden className="size-3" />,
		CHECKLIST: <List aria-hidden className="size-3" />,
		MANUAL: <CursorClick02 aria-hidden className="size-3" />,
	};

	return (
		<StatusBadge
			label={
				<span className="inline-flex items-center gap-1">
					{iconByOrigin[origin]}
					{origin}
				</span>
			}
			showDot={false}
		/>
	);
}

function noopSelection(_keys: Set<Key> | "all") {
	/* selection disabled */
}

function mapHistoryItemToUi(
	item: VehicleOdometerHistoryItemResponse
): OdometerHistoryItem {
	return {
		id: String(item.vehicleOdometerId),
		updatedAt: dayjs(item.readingDateTime).format("DD/MM/YYYY - HH:mm"),
		responsible: item.updatedBy ?? "Sistema",
		previousDisplayValue:
			item.previousOdometer !== null
				? `${item.previousOdometer.toLocaleString("pt-BR")}km`
				: "-",
		currentDisplayValue: `${item.newOdometer.toLocaleString("pt-BR")}km`,
		origin: mapOdometerApiSourceToHistoryOrigin(item.source),
		attachmentName: item.hasAttachment ? "Ver evidência" : undefined,
	};
}

export function OdometerTabContent({
	currentOdometer,
	vehicleId,
	vehicleHeaderSubtitle,
}: OdometerTabContentProps) {
	const [selectedOrigins, setSelectedOrigins] = useState<
		OdometerHistoryOrigin[]
	>([]);
	const [selectedAttachmentId, setSelectedAttachmentId] = useState<
		string | null
	>(null);
	const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);
	const [isManualDrawerOpen, setIsManualDrawerOpen] = useState(false);
	const [currentHistoryPage, setCurrentHistoryPage] = useState(1);
	const { data: odometerSummary } = useGetVehicleOdometerSummary(vehicleId);
	const selectedApiSources = useMemo(
		() =>
			selectedOrigins.map((origin) => {
				if (origin === "TELEMETRIA") {
					return VEHICLE_ODOMETER_SOURCE.TELEMETRY;
				}
				if (origin === "CHECKLIST") {
					return VEHICLE_ODOMETER_SOURCE.CHECKLIST;
				}
				return VEHICLE_ODOMETER_SOURCE.MANUAL;
			}),
		[selectedOrigins]
	);

	const historyQuery = useGetVehicleOdometerHistory(vehicleId, {
		sources: selectedApiSources,
		pageNumber: currentHistoryPage,
		pageSize: HISTORY_PAGE_SIZE,
		sortBy: "ReadingDateTime",
		sortDirection: "desc",
	});
	const {
		data: historyResponse,
		isFetching: isFetchingHistory,
		isError: isHistoryError,
		refetch: refetchHistory,
	} = historyQuery;

	const { total: totalHistoryPages } = usePaginationTotal({
		page: currentHistoryPage,
		onPageChange: setCurrentHistoryPage,
		criteria: {
			vehicleId,
			sources: selectedApiSources,
			pageSize: HISTORY_PAGE_SIZE,
		},
		totalPages: historyResponse?.totalPages,
		query: historyQuery,
	});

	const { data: latestChecklistData, isLoading: isLoadingLatestChecklist } =
		useGetVehicleOdometerLatestChecklist(vehicleId, isChecklistModalOpen);

	const { data: selectedAttachmentDetail } = useGetVehicleOdometerDetail(
		vehicleId,
		selectedAttachmentId ?? "",
		!!selectedAttachmentId
	);

	const filteredHistory = useMemo<OdometerHistoryItem[]>(() => {
		if (!historyResponse?.items || historyResponse.items.length === 0) {
			return [];
		}
		return historyResponse.items.map(mapHistoryItemToUi);
	}, [historyResponse]);

	const historyColumns: TableColumn<OdometerHistoryItem>[] = useMemo(
		() => [
			{
				id: "updatedAt",
				label: "Data e Hora",
				render: (row) => row.updatedAt,
			},
			{
				id: "responsible",
				label: "Responsável",
				render: (row) => (
					<div className="flex flex-col">
						<span className="font-medium text-fg-primary text-sm">
							{row.responsible}
						</span>
					</div>
				),
			},
			{
				id: "fromTo",
				label: "De → Para",
				render: (row) => (
					<div className="flex items-center gap-2">
						<span className="text-fg-secondary text-sm line-through">
							{row.previousDisplayValue}
						</span>
						<ArrowRight className="size-3.5 text-fg-quaternary" />
						<span className="font-bold text-fg-primary text-sm">
							{row.currentDisplayValue}
						</span>
					</div>
				),
			},
			{
				id: "origin",
				label: "Origem",
				render: (row) => <HistoryOriginBadge origin={row.origin} />,
			},
			{
				id: "attachment",
				label: "Anexo",
				render: (row) =>
					row.attachmentName ? (
						<ButtonUtility
							aria-label={`Visualizar anexo ${row.attachmentName}`}
							className="text-fg-primary"
							color="tertiary"
							icon={Attachment02}
							onClick={() => setSelectedAttachmentId(row.id)}
							size="sm"
							tooltip="Visualizar anexo"
						/>
					) : (
						<span className="text-fg-disabled">-</span>
					),
			},
		],
		[]
	);

	const parsedCurrentOdometer =
		odometerSummary?.currentOdometer ?? currentOdometer ?? 0;
	const isTelemetryActive = odometerSummary?.hasTelemetry ?? false;
	const isOdometerOutdated = odometerSummary?.isOdometerOutdated ?? false;

	const toggleOrigin = (origin: OdometerHistoryOrigin) => {
		setCurrentHistoryPage(1);
		setSelectedOrigins((prev) => {
			if (prev.includes(origin)) {
				return prev.filter((item) => item !== origin);
			}
			return [...prev, origin];
		});
	};

	const historyView = resolveTableViewState({
		itemCount: filteredHistory.length,
		isLoading: isFetchingHistory,
		isError: isHistoryError,
	});
	const retryHistory = () => {
		refetchHistory();
	};

	const historyTableContent: ReactNode =
		historyView.view === "data" ? (
			<>
				{historyView.hasRefreshError ? (
					<TableStateRow
						colSpan={historyColumns.length}
						onRetry={retryHistory}
						state="refresh-error"
					/>
				) : null}
				{filteredHistory.map((item) => (
					<Table.Row
						columns={historyColumns}
						dependencies={[item]}
						id={item.id}
						key={item.id}
					>
						{(column) => (
							<Table.Cell className="whitespace-nowrap" key={column.id}>
								{column.render ? column.render(item) : ""}
							</Table.Cell>
						)}
					</Table.Row>
				))}
			</>
		) : (
			<TableStateRow
				colSpan={historyColumns.length}
				emptyContent="Nenhuma atualização encontrada para os filtros selecionados."
				errorMessage="Não foi possível carregar o histórico do hodômetro."
				loadingLabel="Carregando histórico"
				onRetry={retryHistory}
				state={historyView.view}
			/>
		);

	return (
		<div className="mx-6 flex flex-col gap-5">
			<div className="rounded-[20px] border border-secondary_alt bg-bg-secondary p-6">
				<PageHeader
					actions={
						<StatusBadge
							label={
								isOdometerOutdated
									? "Hodômetro desatualizado"
									: "Hodômetro atualizado"
							}
							palette={
								isOdometerOutdated
									? STATUS_BADGE_TONE_PALETTES.error
									: STATUS_BADGE_TONE_PALETTES.success
							}
						/>
					}
					className="mb-4"
					subtitle="Defina algumas regras do hodômetro da conta"
					title="Configuração de Hodômetro"
					titleAs="h2"
				/>

				<div className="rounded-xl border border-border-secondary bg-white p-5">
					<div className="mb-4 flex items-start gap-3">
						<div className="rounded-full bg-[#e9eaeb] p-3">
							<GitBranch01 className="size-6" />
						</div>
						<div>
							<h3 className="font-bold text-fg-primary text-md">
								Hierarquia das Fontes
							</h3>
							<p className="text-quaternary text-sm">
								Visualize a ordem das fontes de dados do hodômetro do veículo
							</p>
						</div>
					</div>

					<div className="flex flex-col gap-3">
						<SourceCard
							description="Dados nativos do rastreador e CAN-BUS."
							icon={<Signal01 className="size-6 stroke-[1.3]" />}
							rightSlot={
								<div className="flex flex-col items-end gap-4">
									<StatusBadge
										label={isTelemetryActive ? "Ativa" : "Inativa"}
										palette={
											isTelemetryActive
												? STATUS_BADGE_TONE_PALETTES.success
												: STATUS_BADGE_TONE_PALETTES.error
										}
									/>
									{isTelemetryActive ? null : (
										<Button
											aria-label="Contratar Telemetria"
											color="secondary"
											onClick={() =>
												window.open(TELEMETRY_HIRE_WHATSAPP_URL, "_blank")
											}
											size="sm"
										>
											Contratar Telemetria
										</Button>
									)}
								</div>
							}
							title="1. Telemetria (API/Hardware)"
						/>
						<SourceCard
							description="Entrada manual via Checklist do veículo."
							icon={<List className="size-6 stroke-[1.3]" />}
							rightSlot={
								<Button
									aria-label="Atualizar via Checklist"
									onClick={() => setIsChecklistModalOpen(true)}
									size="sm"
								>
									Atualizar via Checklist
								</Button>
							}
							title="2. Checklist"
						/>
						<SourceCard
							description="Entrada manual via preenchimento de formulário."
							icon={<CursorClick02 className="size-6 stroke-[1.3]" />}
							rightSlot={
								<Button
									aria-label="Atualizar manualmente"
									onClick={() => setIsManualDrawerOpen(true)}
									size="sm"
								>
									Atualizar Manualmente
								</Button>
							}
							title="3. Manual"
						/>
					</div>
				</div>
			</div>

			<div className="rounded-[20px] border border-secondary_alt bg-bg-secondary p-6">
				<div className="mb-5 flex flex-col justify-between gap-3 md:flex-row md:items-center">
					<div className="flex items-start gap-3">
						<div className="rounded-full bg-[#e9eaeb] p-3">
							<File05 className="size-6" />
						</div>
						<div>
							<h3 className="font-bold text-fg-primary text-md">
								Histórico de Alterações
							</h3>
							<p className="text-fg-secondary text-sm">
								Veja as principais atividades ligadas a esse hodômetro
							</p>
						</div>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						{HISTORY_ORIGIN_OPTIONS.map((origin) => (
							<Button
								aria-label={`Filtrar origem ${origin}`}
								aria-pressed={selectedOrigins.includes(origin)}
								className={cx("capitalize")}
								color={
									selectedOrigins.includes(origin) ? "primary" : "secondary"
								}
								key={origin}
								onClick={() => toggleOrigin(origin)}
								size="sm"
							>
								{origin.toLowerCase()}
							</Button>
						))}
					</div>
				</div>

				<TableCard.Root className="mt-2" variant="listing">
					<Table
						aria-label="Histórico de alterações do hodômetro"
						isRefreshing={historyView.isRefreshing}
						onSelectionChange={noopSelection}
						selectedKeys={new Set()}
						selectionMode="none"
					>
						<Table.Header columns={historyColumns}>
							{(column) => (
								<Table.Head
									id={column.id}
									isRowHeader={historyColumns.indexOf(column) === 0}
									key={column.id}
									label={column.label}
								/>
							)}
						</Table.Header>
						<Table.Body>{historyTableContent}</Table.Body>
					</Table>
					{!!totalHistoryPages && totalHistoryPages > 1 ? (
						<PaginationPageDefault
							className="p-5"
							onPageChange={setCurrentHistoryPage}
							page={currentHistoryPage}
							total={totalHistoryPages}
						/>
					) : null}
				</TableCard.Root>
			</div>

			{selectedAttachmentId ? (
				<OdometerAttachmentPreviewModal
					detail={selectedAttachmentDetail}
					onClose={() => setSelectedAttachmentId(null)}
				/>
			) : null}

			{isChecklistModalOpen ? (
				<OdometerChecklistUpdateModal
					isLoadingLatestChecklist={isLoadingLatestChecklist}
					latestChecklistData={latestChecklistData}
					onClose={() => setIsChecklistModalOpen(false)}
					onRequestManual={() => setIsManualDrawerOpen(true)}
					vehicleId={vehicleId}
				/>
			) : null}

			<OdometerManualUpdateDrawer
				currentOdometer={parsedCurrentOdometer}
				isOpen={isManualDrawerOpen}
				onOpenChange={setIsManualDrawerOpen}
				vehicleHeaderSubtitle={vehicleHeaderSubtitle}
				vehicleId={vehicleId}
			/>
		</div>
	);
}
